import React from 'react';

import { Box, CircularProgress } from '@mui/material';

import type { NameLookupEntry } from '~/hooks/apis/buildNameLookupMap';
import type { PlaceNameLookup } from '~/hooks/apis/search/useGetPlaces';

import type {
  SearchReproductionEntity,
  SearchReproductionFilter,
  SearchReproductionRow,
} from '~/services/ReproduceSearchService';

import { isGoogleBoundsUrlValue } from '~/utils/lePlaceId';

import type { SearchDetailField } from './SearchDetailSection';

const AIRPORT_PLACE_TYPE = 'airport';
const LANDMARK_PLACE_TYPES = new Set(['point_of_interest', 'landmark']);

type BuildMatchFieldsArgs = {
  destinations: Array<SearchReproductionEntity>;
  landmarks: Array<SearchReproductionEntity>;
  properties: Array<SearchReproductionEntity>;
  placeNamesById: Map<string, PlaceNameLookup>;
};

export function renderLookupName(lookup: NameLookupEntry | undefined, fallback: React.ReactNode): React.ReactNode {
  if (!lookup || lookup.isLoading) {
    return <CircularProgress size={16} />;
  }

  if (lookup.isError || !lookup.name) {
    return fallback;
  }

  return lookup.name;
}

function renderEntityName(entity: SearchReproductionEntity): React.ReactNode {
  if (isGoogleBoundsUrlValue(entity.id)) {
    return 'Map area';
  }

  return entity.name;
}

function renderEntityList(entities: Array<SearchReproductionEntity>) {
  if (!entities.length) {
    return '';
  }

  if (entities.length === 1) {
    return renderEntityName(entities[0]);
  }

  return (
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
      {entities.map((entity) => (
        <Box component="li" key={entity.id} sx={{ typography: 'body2' }}>
          {renderEntityName(entity)}
        </Box>
      ))}
    </Box>
  );
}

function renderValueList(values: Array<string>) {
  if (!values.length) {
    return '';
  }

  if (values.length === 1) {
    return values[0];
  }

  return (
    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
      {values.map((value) => (
        <Box component="li" key={value} sx={{ typography: 'body2' }}>
          {value}
        </Box>
      ))}
    </Box>
  );
}

function dedupeEntitiesById(entities: Array<SearchReproductionEntity>): Array<SearchReproductionEntity> {
  const seen = new Set<string>();

  return entities.filter((entity) => {
    if (seen.has(entity.id)) {
      return false;
    }

    seen.add(entity.id);
    return true;
  });
}

function partitionDestinations(
  destinations: Array<SearchReproductionEntity>,
  placeNamesById: Map<string, PlaceNameLookup>,
) {
  const airports: Array<SearchReproductionEntity> = [];
  const landmarksFromDestinations: Array<SearchReproductionEntity> = [];
  const places: Array<SearchReproductionEntity> = [];

  destinations.forEach((entity) => {
    if (isGoogleBoundsUrlValue(entity.id)) {
      places.push(entity);
      return;
    }

    const type = placeNamesById.get(entity.id)?.type;

    if (type === AIRPORT_PLACE_TYPE) {
      airports.push(entity);
      return;
    }

    if (type && LANDMARK_PLACE_TYPES.has(type)) {
      landmarksFromDestinations.push(entity);
      return;
    }

    places.push(entity);
  });

  return { airports, landmarksFromDestinations, places };
}

export function buildMatchFields({
  destinations,
  landmarks,
  properties,
  placeNamesById,
}: BuildMatchFieldsArgs): Array<SearchDetailField> {
  const { airports, landmarksFromDestinations, places } = partitionDestinations(destinations, placeNamesById);
  const allLandmarks = dedupeEntitiesById([...landmarks, ...landmarksFromDestinations]);

  return [
    {
      label: 'Properties',
      value: renderEntityList(properties),
    },
    {
      label: 'Landmarks',
      value: renderEntityList(allLandmarks),
    },
    {
      label: 'Airports',
      value: renderEntityList(airports),
    },
    {
      label: 'Places',
      value: renderEntityList(places),
    },
  ];
}

function formatFilterTypeLabel(type: string | null): string {
  if (!type) {
    return 'Other';
  }

  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getFilterDisplayValue(filter: SearchReproductionFilter): string {
  return formatCellValue(filter.label) || formatCellValue(filter.value);
}

function groupFiltersByType(filters: Array<SearchReproductionFilter>): Map<string, Array<string>> {
  const grouped = new Map<string, Array<string>>();

  filters.forEach((filter) => {
    const displayValue = getFilterDisplayValue(filter);
    if (!displayValue) {
      return;
    }

    const typeKey = filter.type ?? '';
    const values = grouped.get(typeKey) ?? [];
    values.push(displayValue);
    grouped.set(typeKey, values);
  });

  return grouped;
}

export function buildFilterFields(row: SearchReproductionRow): Array<SearchDetailField> {
  const groupedFilters = groupFiltersByType(row.filters);

  return [...groupedFilters.entries()].map(([typeKey, values]) => ({
    label: formatFilterTypeLabel(typeKey || null),
    value: renderValueList(values),
  }));
}

export function formatCellValue(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value);
}
